import { useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/joy";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { getRecordingUrl } from "../utils";

// Helper function to convert durations array to chapter cues
const durationsToChapters = (durations) => {
  if (!durations || durations.length === 0) return [];
  
  const chapters = [];
  let startTime = 0;
  
  durations.forEach((duration, index) => {
    chapters.push({
      startTime: startTime,
      endTime: startTime + duration,
      text: `Section ${index + 1}`
    });
    startTime += duration + 0.1;
  });
  
  return chapters;
};

// Helper function to format time for WebVTT
const formatVTTTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 1000);
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
};

// VideoJS wrapper component.
// Now supports playback speed (rate) control via the `playbackRates` prop or
// by supplying `playbackRates` inside the passed `options` object.
// If neither is provided, sensible defaults are added so the speed menu appears.
// Supports chapters via the `chapters` prop (array of {startTime, endTime, text} objects)
// or via `durations` prop (array of durations in seconds that will be converted to chapters).
export const VideoJS = ({ options, onReady, playbackRates, chapters, durations }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode. 
      const videoElement = document.createElement("video-js");

      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current.appendChild(videoElement);

      // Merge playbackRates: explicit prop > options.playbackRates > defaults
      const mergedOptions = {
        controls: true,
        ...options,
        playbackRates: playbackRates || options?.playbackRates || [0.5, 0.75, 1, 1.25, 1.5, 2]
      };

      const player = playerRef.current = videojs(videoElement, mergedOptions, () => {
        videojs.log('player is ready');
        
        // Add chapters track if provided
        const chapterData = chapters || (durations ? durationsToChapters(durations) : null);
        if (chapterData && chapterData.length > 0) {
          // Create a VTT file content
          let vttContent = 'WEBVTT\n\n';
          chapterData.forEach((chapter, index) => {
            vttContent += `${index + 1}\n`;
            vttContent += `${formatVTTTime(chapter.startTime)} --> ${formatVTTTime(chapter.endTime)}\n`;
            vttContent += `${chapter.text}\n\n`;
          });
          
          // Create a blob URL for the VTT content
          const blob = new Blob([vttContent], { type: 'text/vtt' });
          const vttUrl = URL.createObjectURL(blob);
          
          // Add the chapters track
          player.addRemoteTextTrack({
            kind: 'chapters',
            src: vttUrl,
            label: 'Chapters',
            default: true
          }, false);
          
          // Add visual markers on the progress bar for each chapter
          player.ready(() => {
            const progressControl = player.controlBar.progressControl;
            if (progressControl) {
              const seekBar = progressControl.seekBar;
              const duration = player.duration();
              
              // Wait for duration to be available
              const addMarkers = () => {
                const actualDuration = player.duration();
                if (actualDuration && actualDuration > 0) {
                  chapterData.forEach((chapter, index) => {
                    if (index > 0) { // Skip first marker (start of video)
                      const markerPosition = (chapter.startTime / actualDuration) * 100;
                      const markerEl = document.createElement('div');
                      markerEl.className = 'vjs-marker';
                      markerEl.style.left = `${markerPosition}%`;
                      markerEl.title = chapter.text;
                      seekBar.el().appendChild(markerEl);
                    }
                  });
                }
              };
              
              if (duration > 0) {
                addMarkers();
              } else {
                player.one('loadedmetadata', addMarkers);
              }
            }
          });
        }
        
        onReady && onReady(player);
      });

      // You could update an existing player in the `else` block here
      // on prop change, for example:
    } else {
      const player = playerRef.current;

      // Update dynamic basics (avoid full dispose/re-init for now).
      if (options?.autoplay !== undefined) player.autoplay(options.autoplay);
      if (options?.sources) player.src(options.sources);

      // If playbackRates change (rare), reconfigure menu if possible.
      const desiredRates = playbackRates || options?.playbackRates;
      if (desiredRates && player.playbackRates && typeof player.playbackRates === 'function') {
        try {
          // Some versions expose player.options_ to mutate; safer to dispose & recreate but keep lightweight.
          // We'll only attempt if menu exists; otherwise ignore.
          if (player.controlBar?.playbackRateMenuButton) {
            // No direct API to replace the list; simplest is to rebuild via dispose & re-init.
            const currentRates = player.controlBar.playbackRateMenuButton.playbackRates_ || [];
            const differ = JSON.stringify(currentRates) !== JSON.stringify(desiredRates);
            if (differ) {
              const currentTime = player.currentTime();
              const wasPaused = player.paused();
              const src = player.currentSource();
              player.dispose();
              const videoElement = document.createElement('video-js');
              videoElement.classList.add('vjs-big-play-centered');
              videoRef.current.innerHTML = '';
              videoRef.current.appendChild(videoElement);
              const mergedOptions = {
                controls: true,
                ...options,
                sources: src ? [src] : options?.sources,
                playbackRates: desiredRates
              };
              const newPlayer = playerRef.current = videojs(videoElement, mergedOptions, () => {
                if (currentTime) newPlayer.currentTime(currentTime);
                if (!wasPaused) newPlayer.play();
                onReady && onReady(newPlayer);
              });
            }
          }
        } catch (e) {
          // Fail silently; playback rate menu update isn't critical.
          console.warn('PlaybackRates update skipped:', e);
        }
      }
    }
  }, [options, onReady, playbackRates, chapters, durations]);

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <Box 
      data-vjs-player
      sx={{
        // Container styling
        '& .video-js': {
          borderRadius: 'var(--joy-radius-lg)',
          overflow: 'hidden',
          fontFamily: 'var(--joy-fontFamily-body)',
        },

        // Big play button - properly circular
        '& .vjs-big-play-button': {
          width: '2.5em',
          height: '2.5em',
          lineHeight: '2.5em',
          borderRadius: '50%',
          marginTop: '-1.25em',
          marginLeft: '-1.25em',
          backgroundColor: 'var(--joy-palette-primary-400)',
          border: 'none',
          
          '&:hover': {
            backgroundColor: 'var(--joy-palette-primary-500)',
          },
        },

        // Control bar - light modern style
        '& .vjs-control-bar': {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          
          '& .vjs-control': {
            color: 'rgba(0, 0, 0, 0.75) !important',
          },
          
          '& .vjs-current-time, & .vjs-duration, & .vjs-time-divider': {
            color: 'rgba(0, 0, 0, 0.75) !important',
          },
          
          '& .vjs-remaining-time': {
            color: 'rgba(0, 0, 0, 0.75) !important',
          },
        },

        // Progress bar styling
        '& .vjs-progress-holder': {
          backgroundColor: 'rgba(0, 0, 0, 0.15)',
        },

        '& .vjs-play-progress': {
          backgroundColor: 'var(--joy-palette-primary-400)',
        },

        '& .vjs-load-progress': {
          backgroundColor: 'rgba(0, 0, 0, 0.25)',
        },

        // Volume bar
        '& .vjs-volume-level': {
          backgroundColor: 'var(--joy-palette-primary-400)',
        },

        // Chapter markers
        '& .vjs-marker': {
          position: 'absolute',
          bottom: -1,
          width: 3,
          height: 5,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          pointerEvents: 'none',
          zIndex: 1,
        },

        '& .vjs-progress-control:hover .vjs-marker': {
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          height: 7,
        },

        // Menus - force white background
        '& .vjs-menu': {
          backgroundColor: 'white !important',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
        
        '& .vjs-menu-button-popup .vjs-menu': {
          backgroundColor: 'white !important',
        },
        
        '& .vjs-menu-content': {
          backgroundColor: 'white !important',
        },
        
        '& .vjs-menu li.vjs-menu-item': {
          color: 'rgba(0, 0, 0, 0.87) !important',
          backgroundColor: 'white !important',
          transition: 'all 0.2s ease',
        },
        
        '& .vjs-menu li.vjs-menu-item:hover': {
          backgroundColor: 'rgba(59, 59, 205, 0.08) !important',
          color: 'var(--joy-palette-primary-600) !important',
        },
        
        '& .vjs-menu li.vjs-menu-item.vjs-selected': {
          backgroundColor: 'var(--joy-palette-primary-500) !important',
          color: 'white !important',
        },
        
        '& .vjs-menu li.vjs-menu-item.vjs-selected:hover': {
          backgroundColor: 'var(--joy-palette-primary-600) !important',
          color: 'white !important',
        },

        // Time tooltip
        '& .vjs-time-tooltip': {
          backgroundColor: 'rgba(0, 0, 0, 0.85) !important',
          color: 'white !important',
          backdropFilter: 'blur(6px)',
          padding: '0.35em 0.6em',
          fontSize: '0.9em',
          fontWeight: 700,
          borderRadius: '6px',
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.45)',
        },
      }}
    >
      <div ref={videoRef} />
    </Box>
  );
}

const getVideoJsOptions = id => ({
  autoplay: false,
  controls: true,
  responsive: true,
  fluid: true,
  sources: [{
    src: getRecordingUrl(id),
    type: 'video/mp4'
  }]
});

export const Video = ({ recordingId, durations = [] }) => {
  // console.log("Video durations:", durations);
  const options = useMemo(() => getVideoJsOptions(recordingId), [recordingId]);
  return <VideoJS options={options} durations={durations} />;
}

export default VideoJS;